import { Component, OnInit } from '@angular/core';
import { NgSelectModule } from '@ng-select/ng-select';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { StockWriteOffService } from '../stock-write-off.service';
import { ToastrService } from 'ngx-toastr';
import { InventoryService } from '../../inventory/inventory.service';
import { ProductsService } from '../../products/products.service';
import { ReasonsService } from '../../transfer-out-list/reason.service';
import { ConfirmDialogService } from '../../confirm-dialog/confirm-dialog.service';
import { ActivatedRoute } from '@angular/router'; // 👈 Import ActivatedRoute
import { Router } from '@angular/router';
// ExcelUploadComponent is not rendered on this page (the list page owns the upload and routes here with the parsed rows).
import { environment } from '../../../../environments/environment';
import { HttpResponse } from '@angular/common/http';
import {
  debounceTime,
  distinctUntilChanged,
  switchMap,
  of,
  Subject,
  catchError,
  map,
} from 'rxjs';
import { withMatchedEan } from '../../../core/utils/current-ean.util';

@Component({
  selector: 'app-create-stock-write-off',
  standalone: true,
  templateUrl: './create-stock-write-off.component.html',
  styleUrls: ['./create-stock-write-off.component.scss'],
  imports: [CommonModule, FormsModule, NgSelectModule],
})
export class CreateStockWriteOffComponent implements OnInit {
  uploadUrl: string = `${environment.apiUrl}/stock-write-off/upload`;
  loading = false;
  savingDraft = false;

  // 🚩 Added for mode handling
  isViewMode = false;
  isEditMode = false;
  writeOffId: string | null = null;
  // -----------------------------

  storeList: any[] = [];
  locationList: any[] = [];
  reasonList: any[] = [];

  // Properties for NG-SELECT search functionality
  skuSearchResults: any[] = [];
  isSkuSearching = false;
  nameSearchResults: any[] = [];
  isNameSearching = false;
  /** "Search by EAN" box — identity barcode or any price generation's EAN. */
  eanSearchResults: any[] = [];
  isEanSearching = false;

  public skuSearch$ = new Subject<string>();
  public nameSearch$ = new Subject<string>();
  public eanSearch$ = new Subject<string>();

  // selected product, qty and batch
  selectedProduct: any = null;
  addQty = 1;
  selectedBatch: any = null;

  // Proper payload structure matching API requirements
  payload: any = {
    storeId: null,
    locationId: null,
    writeOffNumber: '',
    reasonId: null,
    status: 'DRAFT',
    notes: '',
    items: [],
  };

  // Separate display items for UI (with extra fields)
  displayItems: any[] = [];

  constructor(
    private stockWriteOffService: StockWriteOffService,
    private toastr: ToastrService,
    private inventoryService: InventoryService,
    private productService: ProductsService,
    private reasonService: ReasonsService,
    private confirmDialog: ConfirmDialogService,
    private route: ActivatedRoute,
    private router: Router
  ) { }

  ngOnInit(): void {
    // 🚩 Check URL for view/edit mode
    this.route.queryParams.subscribe((params) => {
      this.isViewMode = params['view'] === 'true';
      this.writeOffId = params['id'] || null;

      if (this.writeOffId && !this.isViewMode) {
        this.isEditMode = true;
      }
    });

    this.loadStores();
    this.loadLocations();
    this.loadReasons();

    if (this.writeOffId) {
      this.loadWriteOffData(this.writeOffId); // Load existing data
    } else {
      this.getNextNumber(); // Only get new number for creation mode
    }

    // Handle data passed from Excel upload on the list page
    const state = typeof history !== 'undefined' ? history.state : null;
    if (state && state.uploadedData) {
      this.handleUploadedData(state.uploadedData);
    }

    // 🚀 Setup SKU typeahead - only enable if not in view mode
    if (!this.isViewMode) {
      this.skuSearch$
        .pipe(
          debounceTime(300),
          distinctUntilChanged(),
          switchMap((q) => {
            if (!q || typeof q !== 'string') {
              this.skuSearchResults = [];
              return of(null);
            }
            // Stock on hand is location-specific: without a location the backend
            // falls back to warehouse stock, which is wrong for a store write-off.
            if (!this.payload.locationId) {
              this.isSkuSearching = false;
              this.skuSearchResults = [];
              return of(null);
            }
            this.isSkuSearching = true;
            return this.productService
              .searchProducts({
                sku: q,
                locationId: this.payload.locationId,
                context: 'INVENTORY_LIST',
              })
              .pipe(
                catchError(() => {
                  this.isSkuSearching = false;
                  this.skuSearchResults = [];
                  return of(null);
                })
              );
          })
        )
        .subscribe({
          next: (res: any) => {
            this.isSkuSearching = false;
            this.skuSearchResults = res?.data || res || [];
          },
          error: () => {
            this.isSkuSearching = false;
            this.skuSearchResults = [];
          },
        });

      // EAN typeahead — same location scoping as the SKU search above.
      this.eanSearch$
        .pipe(
          debounceTime(300),
          distinctUntilChanged(),
          switchMap((q) => {
            if (!q || typeof q !== 'string' || q.trim().length < 3) {
              this.eanSearchResults = [];
              return of(null);
            }
            if (!this.payload.locationId) {
              this.isEanSearching = false;
              this.eanSearchResults = [];
              return of(null);
            }
            this.isEanSearching = true;
            return this.productService
              .searchProducts({
                ean: q.trim(),
                locationId: this.payload.locationId,
                context: 'INVENTORY_LIST',
              })
              .pipe(
                map((res: any) => withMatchedEan(res?.data || [], q)),
                catchError(() => {
                  this.isEanSearching = false;
                  this.eanSearchResults = [];
                  return of(null);
                })
              );
          })
        )
        .subscribe({
          next: (res: any) => {
            this.isEanSearching = false;
            this.eanSearchResults = res || [];
          },
          error: () => {
            this.isEanSearching = false;
            this.eanSearchResults = [];
          },
        });

      // 🚀 Setup Name typeahead - only enable if not in view mode
      this.nameSearch$
        .pipe(
          debounceTime(300),
          distinctUntilChanged(),
          switchMap((q) => {
            if (!q || typeof q !== 'string') {
              this.nameSearchResults = [];
              return of(null);
            }
            // Location-scoped stock (see SKU search above).
            if (!this.payload.locationId) {
              this.isNameSearching = false;
              this.nameSearchResults = [];
              return of(null);
            }
            this.isNameSearching = true;
            return this.productService
              .searchProducts({
                query: q,
                locationId: this.payload.locationId,
                context: 'INVENTORY_LIST',
              })
              .pipe(
                catchError(() => {
                  this.isNameSearching = false;
                  this.nameSearchResults = [];
                  return of(null);
                })
              );
          })
        )
        .subscribe({
          next: (res: any) => {
            this.isNameSearching = false;
            this.nameSearchResults = res?.data || res || [];
          },
          error: () => {
            this.isNameSearching = false;
            this.nameSearchResults = [];
          },
        });
    }
  }

  // ------------------------- Data Loading -------------------------

  handleUploadedData(data: any): void {
    console.log('Handling uploaded Excel data:', data);

    if (data.metadata) {
      this.payload.storeId = data.metadata.storeId || this.payload.storeId;
      this.payload.locationId = data.metadata.locationId || this.payload.locationId;
      this.payload.notes = data.metadata.notes || this.payload.notes;
    }

    if (data.items && Array.isArray(data.items)) {
      this.displayItems = data.items.map((item: any) => ({
        productVariantId: item.productVariantId || item.variantId,
        quantity: Number(item.quantity) || 1,
        batchNumber: item.batchNumber || '',
        costPrice: Number(item.costPrice || item.unitCost) || 0,
        sellingPrice: Number(item.sellingPrice) || 0,
        mrp: Number(item.mrp) || 0,
        reasonDetail: item.reasonDetail || '',
        sku: item.skuCode || item.sku || 'N/A', // 👈 Updated to map skuCode
        description: item.name || item.productName || 'Imported via Excel',
        stock: 0,
        currentStock: 0
      }));
    }
  }

  loadWriteOffData(id: string): void {
    this.loading = true;
    this.stockWriteOffService.getWriteOffById(id).subscribe({
      next: (res: any) => {
        this.loading = false;

        // Map API response to component properties
        this.payload.id = res.id;
        this.payload.storeId = res.storeId;
        this.payload.locationId = res.locationId;
        this.payload.writeOffNumber = res.writeOffNumber;
        this.payload.reasonId = res.reasonId;
        this.payload.status = res.status;
        this.payload.notes = res.notes;

        // Map items to displayItems (with extra UI fields)
        this.displayItems = (res.items || []).map((item: any) => ({
          productVariantId: item.variantId,
          quantity: Number(item.quantity) || 0,
          batchNumber: item.batchNumber || '',
          costPrice: item.costPrice || 0,
          reasonDetail: item.reasonDetail || '',
          sellingPrice: Number(item.sellingPrice) || 0,
          mrp: Number(item.mrp) || 0,
          currentStock: item.variant?.soh ?? 0, // if available later
          stock: item.variant?.soh ?? 0,
          sku: item.variant?.product?.sku || 'N/A',
          description: item.variant?.product?.name || 'N/A',
        }));

        console.log(`Write-Off loaded: ${JSON.stringify(res)}`);
        console.log(`Items: ${JSON.stringify(this.displayItems)}`);
        this.toastr.success('Write-Off loaded successfully');
      },
      error: (err) => {
        this.loading = false;
        console.error('Load Write-Off error:', err);
        this.toastr.error('Failed to load Write-Off data');
      },
    });
  }

  loadStores() {
    this.inventoryService.getAllStores().subscribe({
      next: (res: any) => {
        this.storeList = res || [];
        console.log('Stores loaded:', this.storeList);
      },
      error: (err) => {
        console.error('Store load error:', err);
        this.toastr.error('Failed to load stores');
      },
    });
  }

  // ... (Other data loading methods remain the same) ...
  loadLocations() {
    this.stockWriteOffService.getLocations().subscribe({
      next: (res: any) => {
        this.locationList = res || [];
        console.log('All locations loaded:', this.locationList);
      },
      error: (err) => {
        console.error('Location load error:', err);
        this.toastr.error('Failed to load locations');
      },
    });
  }

  onStoreChange(): void {
    // Clear location selection as it might not be valid for the new store
    this.payload.locationId = null;
    // Clear selected product/batches/qty as stock is dependent on store/location
    this.selectedProduct = null;

    this.selectedBatch = null;
    this.addQty = 1;
  }

  onLocationChange(): void {
    // Stock on hand is location-specific, so any product/results picked for the
    // previous location are now stale — clear them so the user re-searches and
    // sees the correct stock for the newly selected location.
    this.selectedProduct = null;
    this.selectedBatch = null;
    this.skuSearchResults = [];
    this.nameSearchResults = [];
    this.eanSearchResults = [];
    this.addQty = 1;
  }

  loadReasons() {
    this.reasonService.getAllReasons().subscribe({
      next: (res: any) => {
        this.reasonList = res?.data || res || [];
        console.log('Reasons loaded:', this.reasonList);
      },
      error: () => {
        // Fallback to default reasons if API doesn't exist
        this.reasonList = [
          { id: 'damaged', name: 'Damaged', shortReason: 'Damaged' },
          { id: 'expired', name: 'Expired', shortReason: 'Expired' },
          { id: 'lost', name: 'Lost', shortReason: 'Lost' },
          { id: 'stolen', name: 'Stolen', shortReason: 'Stolen' },
          { id: 'other', name: 'Other', shortReason: 'Other' },
        ];
        console.log('Using default reasons:', this.reasonList);
      },
    });
  }

  getNextNumber() {
    this.stockWriteOffService.getNextWriteOffNumber().subscribe({
      next: (res: any) => {
        this.payload.writeOffNumber =
          res?.writeOffNumber || res?.nextNumber || '';
        console.log('Write-off number:', this.payload.writeOffNumber);
      },
      error: () => this.toastr.error('Failed to load write-off number'),
    });
  }

  // ------------------------- Search Handlers -------------------------

  // ... (onSkuInput, onNameInput, onSearchResultSelected methods remain the same) ...

  onSkuInput(term: any) {
    if (typeof term === 'string') {
      this.skuSearch$.next(term);
    }
  }

  onNameInput(term: any) {
    if (typeof term === 'string') {
      this.nameSearch$.next(term);
    }
  }

  onEanInput(term: any) {
    if (typeof term === 'string') {
      this.eanSearch$.next(term);
    }
  }

  onSearchResultSelected(product: any) {
    if (product) {
      this.selectedProduct = product;
      // Clear the other search's results list after selection
      this.skuSearchResults = [];
      this.nameSearchResults = [];
      this.eanSearchResults = [];
    } else {
      this.selectedProduct = null;

      this.selectedBatch = null;
    }
  }

  // ------------------------- Item Management -------------------------

  addItem() {
    // Only allow item addition if not in view mode
    if (this.isViewMode) return;

    console.log(`payload ${JSON.stringify(this.payload)}`);

    if (!this.selectedProduct) {
      this.toastr.error('Select a product (by SKU or Name)');
      return;
    }
    if (!this.addQty || this.addQty <= 0) {
      this.toastr.error('Enter a valid quantity');
      return;
    }

    const primaryVariant = this.selectedProduct.variants?.[0];
    const pricingData = primaryVariant?.pricing?.[0];

    if (!primaryVariant?.id) {
      this.toastr.error('Product variant ID is missing. Cannot add item.');
      return;
    }

    // 🚩 Validation: Check if Stock on Hand (SOH) is available
    const soh = primaryVariant?.soh || 0;
    if (soh <= 0) {
      this.toastr.warning('Stock on Hand (SOH) is 0. Cannot add this item.');
      return;
    }
    const batchNumber =
      this.selectedProduct?.variants[0]?.inventory[0]?.batchNumber;

    // Check if already exists with same batch
    const exists = this.displayItems.find(
      (it: any) =>
        it.productVariantId === primaryVariant.id &&
        it.batchNumber === batchNumber
    );
    if (exists) {
      this.toastr.error(
        `Product with batch **${batchNumber}** already added to the list.`
      );
      return;
    }

    const costPrice = Number(pricingData?.costPrice) ?? 0;

    // Add to display items (with extra fields for UI)
    this.displayItems.push({
      productVariantId: primaryVariant.id,
      quantity: Number(this.addQty) || this.addQty,
      batchNumber: batchNumber,
      costPrice: costPrice,
      reasonDetail: '',
      // Extra display fields
      sku: this.selectedProduct.sku || '',
      name: this.selectedProduct.shortDescription || '',
      sellingPrice: Number(pricingData?.sellingPrice) ?? 0,
      mrp: Number(pricingData?.mrp) ?? 0,
      currentStock: primaryVariant?.soh ?? 0,
      batchExpiryDate: this.selectedBatch?.expiryDate || null,
      stock: this.selectedProduct?.variants[0]?.soh || 0,
    });
    console.log(`display : ${JSON.stringify(this.displayItems)}`);

    // Reset inputs
    this.selectedProduct = null;
    this.skuSearchResults = [];
    this.nameSearchResults = [];
    this.addQty = 1;
  }

  removeItem(index: number) {
    // Only allow item removal if not in view mode
    if (this.isViewMode) return;

    this.displayItems.splice(index, 1);
  }

  goToList() {
    this.router.navigate(['/stock-write-off']);
  }

  // ------------------------- Total Calculation Getters (for HTML) -------------------------

  public getTotalQuantity(): number {
    return this.displayItems.reduce(
      (sum: number, item: any) => sum + (Number(item.quantity) || 0),
      0
    );
  }

  public getGrandTotal(): number {
    return this.displayItems.reduce((sum: number, item: any) => {
      const quantity = Number(item.quantity) || 0;
      const cost = Number(item.costPrice) || 0;
      return sum + quantity * cost;
    }, 0);
  }

  public calculateItemTotal(item: any): void {
    if (this.isViewMode) return; // Prevent change in view mode

    if (isNaN(item.quantity) || item.quantity < 1) {
      item.quantity = 1;
    }
    // Validate against batch stock
    if (item.batchStock && item.quantity > item.batchStock) {
      this.toastr.warning(
        `Quantity for ${item.sku} exceeds batch stock (${item.batchStock})`
      );
      item.quantity = item.batchStock;
    }
  }

  // ------------------------- Submission -------------------------
  private buildApiPayload(status: string) {
    const totalQty = this.displayItems.reduce(
      (sum, item) => sum + (Number(item.quantity) || 0),
      0
    );
    const totalValue = this.displayItems.reduce(
      (sum, item) =>
        sum + (Number(item.quantity) || 0) * (Number(item.costPrice) || 0),
      0
    );

    const basePayload: any = {
      // storeId: this.payload.storeId,
      locationId: this.payload.locationId,
      // writeOffNumber is deliberately NOT sent: the value on screen comes
      // from the preview endpoint, which reserves nothing. The server
      // allocates the real number atomically at save time.
      // reasonId: this.payload.reasonId,
      status: status,
      notes: this.payload.notes || '',
      totalQty: totalQty,
      totalValue: totalValue,
      items: this.displayItems.map((item) => ({
        productVariantId: item.productVariantId,
        quantity: item.quantity,
        batchNumber: item.batchNumber || '',
        costPrice: Number(item.costPrice) || 0,
        basePrice: Number(item.costPrice) || 0, // Assuming basePrice is same as costPrice if not available
        sellingPrice: Number(item.sellingPrice) || 0,
        mrp: Number(item.mrp) || 0,
        reasonDetail: item.reasonDetail || '',
      })),
    };

    // ❌ Do not add ID to payload body
    return basePayload;
  }

  saveDraft() {
    if (this.isViewMode) return;

    if (!this.displayItems.length) {
      this.toastr.warning('Please add at least one item');
      return;
    }

    this.savingDraft = true;
    const apiPayload = this.buildApiPayload('DRAFT');
    console.log('Saving draft payload:', apiPayload);

    const apiCall =
      this.isEditMode && this.writeOffId
        ? this.stockWriteOffService.updateWriteOff(this.writeOffId, apiPayload) // 🚩 Call update
        : this.stockWriteOffService.createWriteOff(apiPayload); // Call create

    apiCall.subscribe({
      next: (res: any) => {
        this.savingDraft = false;
        const assigned = res?.writeOffNumber || res?.data?.writeOffNumber;
        this.toastr.success(
          `Draft ${this.isEditMode ? 'updated' : 'saved'} successfully${
            assigned ? ` (${assigned})` : ''
          }`
        );
        // Reset form for creation, or stay on the same view for edit
        if (!this.isEditMode) {
          this.resetForm();
        }
        this.router.navigate(['/stock-write-off']);
      },
      error: (err) => {
        this.savingDraft = false;
        console.error('Draft save error:', err);
        this.toastr.error(err?.error?.message || 'Failed to save draft');
      },
    });
  }

  async submitWriteOff() {
    if (this.isViewMode) return;

    if (!this.displayItems.length) {
      this.toastr.warning('Please add at least one item');
      return;
    }

    // Confirm before posting — a submitted write-off permanently removes stock.
    const confirmed = await this.confirmDialog.confirm({
      title: 'Submit Stock Write-Off?',
      message:
        `You are about to write off <strong>${this.getTotalQuantity()}</strong> unit(s) ` +
        `across <strong>${this.displayItems.length}</strong> item(s), ` +
        `value <strong>₹ ${this.getGrandTotal().toFixed(2)}</strong>.<br>` +
        `This permanently removes the stock and cannot be undone.`,
      confirmText: 'Submit Write-Off',
      cancelText: 'Cancel',
    });
    if (!confirmed) return;

    this.loading = true;

    const apiPayload = this.buildApiPayload('POSTED');
    console.log('Submitting payload:', apiPayload);

    const apiCall =
      this.isEditMode && this.writeOffId
        ? this.stockWriteOffService.updateWriteOff(this.writeOffId, apiPayload) // 🚩 Call update
        : this.stockWriteOffService.createWriteOff(apiPayload); // Call create

    apiCall.subscribe({
      next: (res: any) => {
        this.loading = false;
        // The server assigns the final number, which can differ from the
        // provisional one shown on screen — report what was actually saved.
        const assigned = res?.writeOffNumber || res?.data?.writeOffNumber;
        this.toastr.success(
          assigned
            ? `Stock Write-Off ${assigned} submitted successfully`
            : 'Stock Write-Off submitted successfully'
        );
        this.resetForm();
        this.router.navigate(['/stock-write-off']);
      },

      error: (err) => {
        this.loading = false;
        console.error('Submission error:', err);
        this.toastr.error(err?.error?.message || 'Failed to create write-off');
      },
    });
  }

  private resetForm() {
    this.displayItems = [];
    this.payload.notes = '';
    this.payload.reasonId = null;
    this.selectedProduct = null;
    this.addQty = 1;

    this.selectedBatch = null;
    this.getNextNumber();

    this.isViewMode = false;
    this.isEditMode = false;
    this.writeOffId = null;
  }

  // ------------------------- Excel Upload & Download -------------------------

  downloadTemplate() {
    this.stockWriteOffService.downloadTemplate().subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'stock_write_off_template.xlsx';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.toastr.success('Template downloaded successfully.');
      },
      error: (err) => {
        console.error('Template download error:', err);
        this.toastr.error('Failed to download template.');
      },
    });
  }

  onUploadSuccess(res: any) {
    const data = res?.body?.data || res?.data || res;

    if (Array.isArray(data) && data.length > 0) {
      // Assuming response format: [ { sku, productName, quantity, costPrice, ... }, ... ]
      // Map API response to display items
      const newItems = data.map((item: any) => ({
        productVariantId: item.variantId || item.productVariantId,
        quantity: Number(item.quantity) || item.qty || 1,
        batchNumber: item.batchNumber || '',
        costPrice: Number(item.costPrice) || 0,
        reasonDetail: item.reasonDetail || item.notes || '',
        sku: item.sku || item.variant?.product?.sku || 'N/A',
        name: item.productName || item.variant?.product?.name || 'N/A',
        description: item.productName || item.variant?.product?.name || 'N/A',
        sellingPrice: Number(item.sellingPrice) || 0,
        mrp: Number(item.mrp) || 0,
        stock: item.stock || item.variant?.soh || 0,
      }));

      // Find items already in the list
      newItems.forEach((newItem: any) => {
        const existing = this.displayItems.find((it: any) => it.sku === newItem.sku);
        if (existing) {
          existing.quantity += newItem.quantity;
        } else {
          this.displayItems.push(newItem);
        }
      });

      this.toastr.success('Excel data imported successfully!');
    } else {
      this.toastr.warning('Uploaded Excel file was empty or improperly formatted.');
    }
  }

  onUploadError(error: any) {
    console.error('Excel upload error:', error);
    const errorMessage = error?.error?.message || error?.message || 'Failed to process Excel file.';
    this.toastr.error(errorMessage, 'Upload Error');
  }

  // ── Line grid helpers (design: collapsible header, filter pills, per-row delete, strip) ──
  // UI-only members for the Zenflow kit template; the data flow above is unchanged.
  headerOpen = true;
  lineFilter: 'all' | 'over' = 'all';
  lineQuery = '';

  get selectedLocationName(): string {
    const id = this.payload?.locationId;
    if (!id) return '';
    return this.locationList.find((l) => l.id === id)?.name || '';
  }

  /** Primary variant of the product picked in the search boxes (drives the add strip facts). */
  get pickedVariant(): any {
    return this.selectedProduct?.variants?.[0] || null;
  }

  get pickedPricing(): any {
    return this.pickedVariant?.pricing?.[0] || null;
  }

  get pickedBatch(): string {
    return this.pickedVariant?.inventory?.[0]?.batchNumber || '';
  }

  /** A line asks for more than the stock on hand we know about (unknown / zero stock is not flagged). */
  isOverStock(item: any): boolean {
    const stock = Number(item?.stock || 0);
    const qty = Number(item?.quantity || 0);
    return stock > 0 && qty > stock;
  }

  get overStockCount(): number {
    return this.displayItems.filter((i) => this.isOverStock(i)).length;
  }

  /** Rows shown in the grid after the All / Over stock pills and the text filter. */
  get visibleItems(): { item: any; index: number }[] {
    const q = (this.lineQuery || '').trim().toLowerCase();
    const out: { item: any; index: number }[] = [];
    this.displayItems.forEach((item, index) => {
      if (this.lineFilter === 'over' && !this.isOverStock(item)) return;
      if (q) {
        const hay = `${item?.sku || ''} ${item?.description || ''} ${item?.name || ''} ${item?.batchNumber || ''}`.toLowerCase();
        if (!hay.includes(q)) return;
      }
      out.push({ item, index });
    });
    return out;
  }

  get lineCountLabel(): string {
    const shown = this.visibleItems.length;
    const all = this.displayItems.length;
    const word = all === 1 ? 'line' : 'lines';
    return shown === all ? `${all} ${word}` : `${shown} of ${all} lines`;
  }

  get totalAtSellingPrice(): number {
    return this.displayItems.reduce(
      (n, i) => n + (Number(i?.quantity) || 0) * (Number(i?.sellingPrice) || 0),
      0
    );
  }

  get totalAtMrp(): number {
    return this.displayItems.reduce(
      (n, i) => n + (Number(i?.quantity) || 0) * (Number(i?.mrp) || 0),
      0
    );
  }

  clearLineFilters(): void {
    this.lineFilter = 'all';
    this.lineQuery = '';
  }

  /** Dismiss the add strip without adding the picked product. */
  clearSelectedProduct(): void {
    this.selectedProduct = null;
    this.selectedBatch = null;
    this.skuSearchResults = [];
    this.nameSearchResults = [];
    this.eanSearchResults = [];
    this.addQty = 1;
  }

  async clearWriteOffLines(): Promise<void> {
    if (this.isViewMode || !this.displayItems.length) return;
    const n = this.displayItems.length;
    const proceed = await this.confirmDialog.confirm({
      title: `Clear all ${n} line${n === 1 ? '' : 's'}?`,
      message:
        'Every item and quantity on this write-off will be removed. Search a product or upload a list again to start over.',
      confirmText: 'Clear lines',
      cancelText: 'Cancel',
      tone: 'neutral',
      icon: 'bi-eraser',
    });
    if (!proceed) return;
    this.displayItems = [];
    this.clearLineFilters();
  }
}
